import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import cheron from '../../../assets/cheron.png';

import slider1 from '../../../assets/app/slider1.jpg';
import cheronbg from '../../../assets/chevron-bg.png';

import { useApp } from '../../../context';
import { selectAds } from '../../../utils/advertising';
import { recordImpression, recordClick } from '../../../utils/adEvents';
import { formatMoney } from '../../../utils/pricing';

import "./styles.css";

/**
 * Abbreviated unit labels, matching the cards. A price is meaningless without
 * its currency and unit, so all three are rendered from the resolved advert —
 * never from a stored display string like the old '$189 pp'.
 */
const UNIT_SUFFIX = {
    PER_NIGHT: 'per night',
    PER_PERSON: 'pp',
    PER_PERSON_PER_NIGHT: 'pp / night',
    PER_GROUP: 'total',
};

/**
 * The one slide that ships in the bundle.
 *
 * NOT a nicety. The `.embla*` classes are global and pin the hero height (see
 * the warnings in HomeCardSection/styles.css and ListingDetail/styles.css), so
 * an empty carousel is a COLLAPSED home page, not a missing image. This slide
 * renders whenever the query is still in flight, fails, or returns nothing —
 * which includes the first load after this shipped, before any advert exists.
 */
const FALLBACK_SLIDE = {
    id: 'house-fallback',
    imageUrl: slider1,
    imageAlt: 'A safari camp on the Zambezi at sunset',
    headline: 'Welcome to Vhakacha',
    tagline: 'Book a safari, find a location, enjoy experiences and many more!',
    captionTitle: null,
    captionSubtitle: null,
    cost: null,
    currency: null,
    priceUnit: null,
    href: null,
    external: false,
    ctaLabel: 'More Details',
};

const ProgressiveImage = ({ src, alt }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    // Cloudinary can serve its own blurred placeholder from the same URL.
    // Nothing else can — and the previous version fell through to using the
    // FULL image as its own placeholder, downloading it twice. Adverts are
    // served from Firebase Storage, so that path is now the common one.
    const lowResSrc = typeof src === 'string' && src.includes('res.cloudinary.com')
        ? src.replace('/upload/f_auto,q_auto/', '/upload/f_auto,q_auto,w_50,e_blur:100/')
        : null;

    return (
        <div className="progressive-image-container">
            {lowResSrc && (
                <img
                    src={lowResSrc}
                    alt=""
                    aria-hidden="true"
                    className="carousel-image-placeholder"
                    style={{
                        opacity: isLoaded ? 0 : 1,
                        transition: 'opacity 0.6s ease-in-out'
                    }}
                />
            )}
            <img
                src={src}
                alt={alt}
                className="carousel-image"
                onLoad={() => setIsLoaded(true)}
                style={{
                    opacity: isLoaded ? 1 : 0,
                    transition: 'opacity 0.6s ease-in-out'
                }}
            />
        </div>
    );
};

/** The caption card, minus whichever parts this advert does not carry. */
const Caption = ({ ad }) => {
    // `priceUnit` comes off the bound listing, or off the advert when unbound;
    // either way it is resolved by the time it reaches here. 'pp' only covers
    // a legacy row that predates the field.
    const price = ad.cost != null
        ? `${formatMoney(ad.cost, ad.currency || 'USD')} ${UNIT_SUFFIX[ad.priceUnit] || 'pp'}`
        : null;

    // Nothing to say and nowhere to go — a house slide with only a headline.
    if (!ad.captionTitle && !price && !ad.href) return null;

    return (
        <div className="caption-container">
            <Box className="caption">
                <div className="carousel-chevron">
                    <img src={cheron} alt="" className="carousel-chevron-image" />
                </div>
                <Box className="caption-body">
                    <div className="caption-text">
                        {ad.captionTitle && <p className="text-title">{ad.captionTitle}</p>}
                        {ad.captionSubtitle && <p className="text-location">{ad.captionSubtitle}</p>}
                    </div>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {price && <p className="text-cost">from {price}</p>}
                        {/* An anchor, not an onClick. This button existed for
                            years with no handler at all; as a link, hover shows
                            the destination and middle-click opens a tab. */}
                        {/* recordClick never throws and never awaits — a lost
                            data point is cheap, a swallowed navigation is not. */}
                        {ad.href && (ad.external ? (
                            <Button
                                size="small"
                                className="carousel-view-more"
                                variant="outlined"
                                component="a"
                                href={ad.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => recordClick(ad)}
                            >
                                {ad.ctaLabel}
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                className="carousel-view-more"
                                variant="outlined"
                                component={Link}
                                to={ad.href}
                                onClick={() => recordClick(ad)}
                            >
                                {ad.ctaLabel}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

export default function MainCarousel() {
    const { adverts, serviceListing, listingsLoaded } = useApp();

    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 }, [
        Autoplay({ delay: 7000, stopOnInteraction: false }),
        Fade()
    ]);

    const listingsById = useMemo(() => {
        const map = {};
        (serviceListing || []).forEach((l) => { map[l.id] = l; });
        return map;
    }, [serviceListing]);

    const slides = useMemo(() => {
        const selected = selectAds(adverts, 'HOME_CAROUSEL', {
            listingsById,
            // Bound adverts are held rather than dropped while the listings
            // snapshot is still in flight — otherwise the hero shows the
            // fallback and then visibly swaps on every cold load.
            listingsLoaded,
        });
        return selected.length ? selected : [FALLBACK_SLIDE];
    }, [adverts, listingsById, listingsLoaded]);

    /**
     * Manual navigation.
     *
     * `autoplay.reset()` restarts the timer, so pressing Next does not get
     * overtaken by an autoplay tick a moment later — which reads as the
     * carousel skipping a slide the traveller just asked for. Optional-chained
     * because the plugin is only registered once Embla has initialised.
     */
    const scrollPrev = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollPrev();
        emblaApi.plugins()?.autoplay?.reset();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollNext();
        emblaApi.plugins()?.autoplay?.reset();
    }, [emblaApi]);

    /**
     * An impression is recorded when a slide is actually SHOWN, not when the
     * carousel mounts.
     *
     * Counting all seven on mount would credit every advertiser for a
     * traveller who bounced after two seconds having seen one slide. Embla's
     * `select` fires on each autoplay tick and each manual scroll, which is
     * exactly the moment a slide becomes the visible one. `recordImpression`
     * then dedupes per session, so looping past a slide a second time does
     * not count twice.
     */
    useEffect(() => {
        if (!emblaApi) return undefined;

        const report = () => {
            const ad = slides[emblaApi.selectedScrollSnap()];
            if (ad) recordImpression(ad);
        };

        report(); // the slide already on screen
        emblaApi.on('select', report);
        return () => { emblaApi.off('select', report); };
    }, [emblaApi, slides]);

    return (
        <div className="carousel-container">


            <div className="embla" ref={emblaRef} style={{ backgroundColor: "#1a1a1a" }}>
                <div className="embla__container">
                    {slides.map((ad, i) => (
                        <div className="embla__slide" key={`media-item-${ad.id || i}`}>
                            <ProgressiveImage src={ad.imageUrl} alt={ad.imageAlt || ''} />
                            <div className="hero-headline">
                                {ad.headline && <p className="text-title">{ad.headline}</p>}
                                {ad.tagline && <p className="text-location">{ad.tagline}</p>}
                            </div>

                            <div className="overlay-container" />
                            <img src={cheronbg} className="bg-chevron" alt="" />
                            <Caption ad={ad} />
                        </div>
                    ))}
                </div>
            </div>

            {/* Siblings of `.embla`, not children of it. Embla treats the
                viewport's first element child as its slide container, so a
                button inside would be competing with that assumption for no
                benefit — `.carousel-container` is already positioned, and the
                hero headline is absolute, so it has exactly the carousel's
                height to centre against.

                Hidden until there is somewhere to go: with one slide (the
                bundled fallback) the arrows would be controls that do nothing. */}
            {slides.length > 1 && (
                <>
                    <button
                        type="button"
                        className="carousel-arrow carousel-arrow--prev"
                        onClick={scrollPrev}
                        aria-label="Previous slide"
                    >
                        <ChevronLeftIcon />
                    </button>
                    <button
                        type="button"
                        className="carousel-arrow carousel-arrow--next"
                        onClick={scrollNext}
                        aria-label="Next slide"
                    >
                        <ChevronRightIcon />
                    </button>
                </>
            )}
        </div>
    );
}
